import { ATTENDANT_PACKAGES } from "./attendantCareHubConfig";
import { NURSE_PROCEDURES, mergeNurseProcedureMap } from "./nurseCareHubConfig";
import { PHYSIO_PROCEDURES, mergePhysioProcedureMap } from "./physioTherapyCareHubConfig";
import { resolveShiftBookingSelection } from "./careRoleGuidelines";

/** Physio therapy services (customer booking step 1). */
export const PHYSIO_THERAPY_SESSIONS = PHYSIO_PROCEDURES;

const COMPANION_TIER_TO_SERVICE_ID = {
  t2: "companion_2h",
  t4: "companion_4h",
  t6: "companion_6h",
};

export function resolveCareHubRole(caregiver) {
  const raw = String(
    caregiver?.serviceRole ||
      caregiver?.careType ||
      caregiver?.professionalType ||
      caregiver?.category ||
      caregiver?.role ||
      ""
  ).toLowerCase();
  if (raw.includes("nurse")) return "nurses";
  if (raw.includes("physio") || raw.includes("therapist")) return "physio";
  return "attendant";
}

export function getCareHubRoleLabel(role) {
  if (role === "nurses") return "Nurse";
  if (role === "physio") return "Physiotherapist";
  return "Care Attendant";
}

export function getServiceStepTitle(role, { searchShift = "", showPackages = true } = {}) {
  if (!showPackages && (role === "attendant" || role === "nurses")) {
    const shift = String(searchShift || "").toLowerCase();
    if (shift === "12_day") return "Book 12 Hour Day Shift";
    if (shift === "12_night") return "Book 12 Hour Night Shift";
    if (shift === "24h") return "Book 24 Hours Live-In";
    return "Schedule Care Booking";
  }
  if (role === "nurses") return "Select Procedure Service";
  if (role === "physio") return "Select Therapy Service";
  return "Select Care Attendant Service Package";
}

export function resolveAttendantPackagePrice(pack, tierId, caregiverPackages) {
  const offered = caregiverPackages?.[pack?.id];
  if (pack?.pricingMode === "tiers") {
    const n = Number(offered?.tierPrices?.[tierId]);
    if (Number.isFinite(n) && n > 0) return Math.round(n);
    const tier = pack.tiers?.find((t) => t.id === tierId);
    return tier?.defaultPriceInr || 500;
  }
  const n = Number(offered?.priceInr);
  if (Number.isFinite(n) && n > 0) return Math.round(n);
  return pack?.defaultPriceInr || 500;
}

export function resolveNurseProcedurePrice(procedure, caregiver) {
  const map = mergeNurseProcedureMap(
    caregiver?.profileExtended?.nurseCareHub?.procedures ||
      caregiver?.nurseCareHub?.procedures
  );
  const row = map[procedure.id];
  if (row && row.offered === false) return null;
  const saved = Number(row?.priceInr);
  if (Number.isFinite(saved) && saved > 0) return Math.round(saved);
  return procedure.defaultPriceInr || 500;
}

export function resolvePhysioSessionPrice(session, caregiver) {
  const map = mergePhysioProcedureMap(
    caregiver?.profileExtended?.physioCareHub?.procedures ||
      caregiver?.physioCareHub?.procedures
  );
  const row = map[session.id];
  if (row && row.offered === false) return null;
  const saved = Number(row?.priceInr);
  if (Number.isFinite(saved) && saved > 0) return Math.round(saved);
  return session.defaultPriceInr || 500;
}

export function buildServiceSelection({
  role,
  packageKey = "hygiene_personal",
  tierId = "t2",
  procedureId = "injection",
  sessionId = "short_assessment",
  caregiver = null,
  searchShift = "",
}) {
  const caregiverPackages =
    caregiver?.packages || caregiver?.profileExtended?.attendantCareHub?.packages || null;

  const shiftSelection = resolveShiftBookingSelection({ serviceRole: role, searchShift });
  if (shiftSelection && (role === "attendant" || role === "nurses")) {
    // Prefer caregiver Pricing & Plans shift rates — never estimated catalog invents.
    const shiftPricing =
      caregiver?.profileExtended?.shiftPricing || caregiver?.shiftPricing || null;
    const SHIFT_PRICE_KEY = {
      attendant_12h_day: "day12",
      attendant_12h_night: "night12",
      attendant_24h: "live24",
      nurse_12h_day: "day12",
      nurse_12h_night: "night12",
      nurse_24h: "live24",
    };
    const shiftKey = SHIFT_PRICE_KEY[shiftSelection.serviceId];
    const shiftRow =
      shiftKey && shiftPricing && typeof shiftPricing === "object" ? shiftPricing[shiftKey] : null;
    const savedShift = Number(shiftRow?.priceInr);
    if (Number.isFinite(savedShift) && savedShift > 0 && shiftRow?.active !== false) {
      return {
        ...shiftSelection,
        caregiverFeeInr: Math.round(savedShift),
      };
    }
    // Leave null until BE match returns feeSource=shiftPricing.
    return {
      ...shiftSelection,
      caregiverFeeInr: null,
    };
  }

  if (role === "nurses") {
    const procedure = NURSE_PROCEDURES.find((p) => p.id === procedureId) || NURSE_PROCEDURES[0];
    const fee = resolveNurseProcedurePrice(procedure, caregiver);
    return {
      serviceRole: "nurses",
      serviceId: procedure.id,
      serviceLabel: procedure.label,
      durationMinutes: procedure.durationMinutes,
      caregiverFeeInr: fee,
    };
  }

  if (role === "physio") {
    const session = PHYSIO_THERAPY_SESSIONS.find((s) => s.id === sessionId) || PHYSIO_THERAPY_SESSIONS[0];
    const fee = resolvePhysioSessionPrice(session, caregiver);
    return {
      serviceRole: "physio",
      serviceId: session.id,
      serviceLabel: session.label,
      durationMinutes: session.durationMinutes,
      caregiverFeeInr: fee,
    };
  }

  const hygiene = ATTENDANT_PACKAGES.find((p) => p.id === "hygiene_personal");
  const companion = ATTENDANT_PACKAGES.find((p) => p.id === "companion");
  if (packageKey.startsWith("companion:")) {
    const tid = packageKey.split(":")[1] || tierId;
    const serviceId = COMPANION_TIER_TO_SERVICE_ID[tid] || "companion_2h";
    const tier = companion?.tiers?.find((t) => t.id === tid);
    const fee = resolveAttendantPackagePrice(companion, tid, caregiverPackages);
    return {
      serviceRole: "attendant",
      serviceId,
      serviceLabel: `Companion Service (${tier?.label || "1–2 hrs"})`,
      durationMinutes: tid === "t6" ? 360 : tid === "t4" ? 240 : 120,
      caregiverFeeInr: fee,
    };
  }

  const fee = resolveAttendantPackagePrice(hygiene, null, caregiverPackages);
  return {
    serviceRole: "attendant",
    serviceId: "hygiene_personal",
    serviceLabel: hygiene?.title || "Hygiene Care & Personal Assistance",
    durationMinutes: 120,
    caregiverFeeInr: fee,
  };
}

export function getSlotHint(role, searchShift = "") {
  const shift = String(searchShift || "").toLowerCase();
  if (shift === "24h") {
    return "24 hours live-in. Choose a start time between 8:00 AM and 6:00 PM. Duty runs for a full 24 hours from the time you select.";
  }
  if (shift === "12_day") {
    return "12 Hrs Day uses fixed windows only: 7 AM–7 PM, 8 AM–8 PM, or 9 AM–9 PM.";
  }
  if (shift === "12_night") {
    return "12 Hrs Night uses fixed windows only: 7 PM–7 AM, 8 PM–8 AM, or 9 PM–9 AM, next day.";
  }
  if (role === "nurses") {
    return "Nursing procedures are booked in procedure-length slots. Pick a slot below for your selected service.";
  }
  if (role === "physio") {
    return "Physiotherapy sessions use your selected duration. Pick 1 slot below for your therapy block.";
  }
  return "Care Attendants are booked in consecutive 2-hour slots between 7:00 AM and 9:00 PM. Pick 1 slot(s) below to map your 2-hour block.";
}

export { ATTENDANT_PACKAGES, NURSE_PROCEDURES };

/**
 * Hire wizard order:
 * Patient → Care Pass → Shift → Caregiver → Confirm details →
 * send acceptance request (same terms) → wait accept →
 * Agreement → Police verification (12h/24h only) → Done.
 */
export const CAREHUB_HIRE_STEP = {
  PATIENT: 1,
  CARE_PASS: 2,
  SHIFT: 3,
  SLOTS: 4,
  CONFIRM: 5,
  /** @deprecated accept wait now happens on SLOTS; kept for draft migration */
  AWAITING_ACCEPT: 6,
  AGREEMENT: 7,
  PRV: 8,
  DONE: 9,
  /** @deprecated use DONE */
  REQUEST_SENT: 9,
};

export const CAREHUB_HIRE_WIZARD_STEPS = [
  { id: CAREHUB_HIRE_STEP.PATIENT, label: "Patient" },
  { id: CAREHUB_HIRE_STEP.CARE_PASS, label: "Care Pass" },
  { id: CAREHUB_HIRE_STEP.SHIFT, label: "Service" },
  { id: CAREHUB_HIRE_STEP.SLOTS, label: "Caregiver" },
  { id: CAREHUB_HIRE_STEP.CONFIRM, label: "Confirm" },
  { id: CAREHUB_HIRE_STEP.AGREEMENT, label: "Agreement" },
  { id: CAREHUB_HIRE_STEP.PRV, label: "Verify" },
  { id: CAREHUB_HIRE_STEP.DONE, label: "Done" },
];

/**
 * Police Record Verification is offered only for 12h / 24h (live-in) engagements.
 * Hourly bookings skip PRV entirely (marketplace hire + confirm).
 */
export function offersPoliceVerificationForShift(searchShift, durationMinutes = null) {
  const shift = String(searchShift || "").trim().toLowerCase();
  if (shift === "hourly" || shift === "hour" || shift === "4h" || shift === "4") return false;
  if (shift === "12_day" || shift === "12_night" || shift === "24h") return true;
  const mins = Number(durationMinutes);
  if (Number.isFinite(mins) && mins > 0) {
    // Sub-12h visits (typical hourly packs) never require PRV.
    if (mins < 12 * 60) return false;
    return true;
  }
  // Unknown shift with no duration: do not force PRV (safer for hourly packs).
  return false;
}

export function getCareHubHireWizardSteps({ includePrv = true, includeCarePass = true } = {}) {
  return CAREHUB_HIRE_WIZARD_STEPS.filter((s) => {
    if (!includePrv && s.id === CAREHUB_HIRE_STEP.PRV) return false;
    if (!includeCarePass && s.id === CAREHUB_HIRE_STEP.CARE_PASS) return false;
    return true;
  });
}

/** Map older draft step ids onto the current Patient-first wizard. */
export function migrateCareHubHireWizardStep(step, wizardVersion = 1) {
  const n = Number(step) || 1;
  if (Number(wizardVersion) >= 3) {
    // Legacy mid-flow "awaiting accept" maps back onto caregiver step.
    if (n === CAREHUB_HIRE_STEP.AWAITING_ACCEPT) return CAREHUB_HIRE_STEP.SLOTS;
    return Math.min(Math.max(1, n), CAREHUB_HIRE_STEP.DONE);
  }
  if (Number(wizardVersion) >= 2) {
    // v2 ended at REQUEST_SENT = 6
    if (n >= 6) return CAREHUB_HIRE_STEP.DONE;
    return Math.min(Math.max(1, n), CAREHUB_HIRE_STEP.CONFIRM);
  }
  const legacyMap = {
    1: CAREHUB_HIRE_STEP.SHIFT,
    2: CAREHUB_HIRE_STEP.SLOTS,
    3: CAREHUB_HIRE_STEP.SHIFT,
    4: CAREHUB_HIRE_STEP.PATIENT,
    5: CAREHUB_HIRE_STEP.CARE_PASS,
    6: CAREHUB_HIRE_STEP.CONFIRM,
    7: CAREHUB_HIRE_STEP.CONFIRM,
    8: CAREHUB_HIRE_STEP.DONE,
  };
  return legacyMap[n] || CAREHUB_HIRE_STEP.PATIENT;
}

export function getCareHubHireWizardProgress(
  step,
  { includePrv = true, includeCarePass = true } = {}
) {
  const labels = getCareHubHireWizardSteps({ includePrv, includeCarePass });
  const total = labels.length;
  let currentId = migrateCareHubHireWizardStep(step, 3);
  // Hourly (no PRV): treat Verify as Done so progress never lands on a hidden step.
  if (!includePrv && currentId === CAREHUB_HIRE_STEP.PRV) {
    currentId = CAREHUB_HIRE_STEP.DONE;
  }
  // Valid Care Pass: never show Care Pass as the current step in the progress bar.
  if (!includeCarePass && currentId === CAREHUB_HIRE_STEP.CARE_PASS) {
    currentId = CAREHUB_HIRE_STEP.SHIFT;
  }
  const visibleIndex = labels.findIndex((s) => s.id === currentId);
  const displayStep = visibleIndex >= 0 ? visibleIndex + 1 : Math.min(currentId, total);
  return {
    step: displayStep,
    total,
    steps: labels.map((s, i) => ({
      id: s.id,
      label: s.label,
      state: i + 1 === displayStep ? "current" : i + 1 < displayStep ? "done" : "upcoming",
    })),
  };
}
